import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, Plus, Tag, Edit2, Trash2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { DEFAULT_TAGS } from '../mock';
import { profileAPI } from '../services/api';

const PatientTagsManager = ({ onClose }) => {
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [editingTag, setEditingTag] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      const response = await profileAPI.get();
      const profileData = response.data;
      setProfile(profileData);
      const loadedTags = profileData.patientTags || DEFAULT_TAGS;
      setTags(loadedTags);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags(DEFAULT_TAGS);
    }
  };

  const saveTags = async (updatedTags) => {
    try {
      const updatedProfile = { ...profile, patientTags: updatedTags };
      await profileAPI.update(updatedProfile);
      setTags(updatedTags);
      setProfile(updatedProfile);
    } catch (error) {
      console.error('Error saving tags:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron guardar las etiquetas',
        variant: 'destructive',
      });
    }
  };

  const handleAddTag = () => {
    if (!newTagName.trim()) {
      toast({
        title: 'Error',
        description: 'Por favor ingresa un nombre para la etiqueta',
        variant: 'destructive',
      });
      return;
    }

    const newTag = {
      id: Date.now().toString(),
      name: newTagName.trim(),
      color: newTagColor
    };

    const updatedTags = [...tags, newTag];
    saveTags(updatedTags);

    toast({
      title: '✅ Etiqueta creada',
      description: `"${newTagName}" se agregó correctamente`,
    });

    setNewTagName('');
    setNewTagColor('#3B82F6');
  };

  const handleDeleteTag = (tagId) => {
    const updatedTags = tags.filter(t => t.id !== tagId);
    saveTags(updatedTags);
    
    toast({
      title: 'Etiqueta eliminada',
      description: 'La etiqueta se eliminó correctamente',
    });
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setNewTagName(tag.name);
    setNewTagColor(tag.color);
  };

  const handleUpdateTag = () => {
    if (!editingTag || !newTagName.trim()) return;

    const updatedTags = tags.map(t => 
      t.id === editingTag.id 
        ? { ...t, name: newTagName.trim(), color: newTagColor }
        : t
    );
    
    saveTags(updatedTags);
    
    toast({
      title: '✅ Etiqueta actualizada',
      description: 'Los cambios se guardaron correctamente',
    });

    setEditingTag(null);
    setNewTagName('');
    setNewTagColor('#3B82F6');
  };

  const colorPresets = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#84CC16', // lime
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="bg-gradient-to-r from-blue-500 to-green-500 text-white">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl flex items-center">
              <Tag className="w-6 h-6 mr-2" />
              Gestionar Etiquetas de Pacientes
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* Add/Edit Tag Form */}
          <div className="p-4 bg-blue-50 rounded-lg space-y-4">
            <h3 className="font-semibold">
              {editingTag ? 'Editar Etiqueta' : 'Nueva Etiqueta'}
            </h3>
            
            <div className="space-y-2">
              <Label>Nombre de la etiqueta</Label>
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Ej: VIP, Primera vez, etc."
                maxLength={20}
              />
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center space-x-2">
                <Input
                  type="color"
                  value={newTagColor}
                  onChange={(e) => setNewTagColor(e.target.value)}
                  className="w-20 h-10 cursor-pointer"
                />
                <div className="flex space-x-1">
                  {colorPresets.map(color => (
                    <button
                      key={color}
                      onClick={() => setNewTagColor(color)}
                      className="w-8 h-8 rounded border-2 border-gray-300 hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
              <Badge
                style={{ backgroundColor: newTagColor, color: '#fff', border: 'none' }}
              >
                Vista previa: {newTagName || 'Etiqueta'}
              </Badge>
            </div>

            <div className="flex space-x-2">
              {editingTag ? (
                <>
                  <Button
                    onClick={handleUpdateTag}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    Actualizar Etiqueta
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingTag(null);
                      setNewTagName('');
                      setNewTagColor('#3B82F6');
                    }}
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleAddTag}
                  className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Etiqueta
                </Button>
              )}
            </div>
          </div>

          {/* Tags List */}
          <div className="space-y-4">
            <h3 className="font-semibold">Etiquetas existentes ({tags.length})</h3>
            
            {tags.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Tag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No hay etiquetas creadas</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {tags.map(tag => (
                  <div
                    key={tag.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:shadow-md transition-shadow"
                  >
                    <Badge
                      style={{ backgroundColor: tag.color, color: '#fff', border: 'none' }}
                      className="text-sm px-3 py-1"
                    >
                      {tag.name}
                    </Badge>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditTag(tag)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTag(tag.id)}
                        className="hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4">
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full"
            >
              Cerrar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientTagsManager;